"use client";

import type { QuestionnaireQuestion } from "@/app/directories/shop/[clientSlug]/types";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { Button } from "@frontend/shared/components/ui/button";
import {
  CustomDialogContent,
  Dialog,
  DialogClose,
  DialogHeader,
  DialogTitle,
} from "@frontend/shared/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useRouter } from "next/navigation";
import type React from "react";
import { useEffect, useState } from "react";

interface QuestionStepProps {
  question: QuestionnaireQuestion;
  questionKey: string;
  onOptionSelect: (optionIndex: number) => void;
  orgId: number;
}

const QuestionStep = ({
  question,
  questionKey,
  onOptionSelect,
  orgId,
}: QuestionStepProps) => {
  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const primaryColor = organizationAssets?.primaryColor || "#000000";

  return (
    <div className="flex flex-col gap-12 w-full h-full justify-start items-start p-4">
      <div className="w-full pr-4">
        <label htmlFor={questionKey} className="text-3xl font-medium">
          {question.text}
        </label>
      </div>
      <div className="flex flex-row gap-8 w-full">
        {question.options.map((option, index) => (
          <Button
            data-testid={`questionnaire-option-${option.text}`}
            key={`${questionKey}-option-${option.text}-${index}`}
            className={`rounded-full py-6 ${
              question.options.length === 1
                ? "w-full"
                : question.options.length === 2
                  ? "w-1/2"
                  : "flex-1"
            }`}
            onClick={() => onOptionSelect(index)}
            variant={index === 0 ? "default" : "outline"}
            style={index === 0 ? { backgroundColor: primaryColor } : undefined}
          >
            {option.text}
          </Button>
        ))}
      </div>
    </div>
  );
};

export const QuestionnaireModal = ({
  open,
  setOpen,
  questions,
  orgId,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  questions: QuestionnaireQuestion;
  orgId: number;
}) => {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CustomDialogContent className="sm:rounded-3xl lg:w-1/3 w-[90%]">
        <DialogTitle className="sr-only">Questionnaire</DialogTitle>
        <DialogClose
          className="absolute right-8 top-8 rounded-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          tabIndex={-1}
        >
          <X className="h-8 w-8" />
          <span className="sr-only">Close</span>
        </DialogClose>
        <DialogHeader>
          <QuestionnaireModalContent
            open={open}
            setOpen={setOpen}
            questions={questions}
            orgId={orgId}
          />
        </DialogHeader>
      </CustomDialogContent>
    </Dialog>
  );
};

const QuestionnaireModalContent = ({
  open,
  setOpen,
  questions,
  orgId,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  questions: QuestionnaireQuestion;
  orgId: number;
}) => {
  const [currentQuestion, setCurrentQuestion] =
    useState<QuestionnaireQuestion>(questions);
  const [questionHistory, setQuestionHistory] = useState<
    QuestionnaireQuestion[]
  >([questions]);
  const router = useRouter();

  // Reset to initial question when modal opens
  useEffect(() => {
    if (open) {
      setCurrentQuestion(questions);
      setQuestionHistory([questions]);
    }
  }, [open, questions]);

  const handleOptionSelect = (optionIndex: number) => {
    const selectedOption = currentQuestion.options[optionIndex];

    if (selectedOption.type === "route") {
      // Navigate to the route and close the modal
      router.push(selectedOption.value as string);
      setOpen(false);
    } else if (selectedOption.type === "question") {
      // Move to the next question
      const nextQuestion = selectedOption.value as QuestionnaireQuestion;
      setCurrentQuestion(nextQuestion);
      setQuestionHistory([...questionHistory, nextQuestion]);
    }
  };

  // Generate a unique key for the current question based on its position in history
  const questionKey = `question-${questionHistory.length - 1}-${currentQuestion.text}`;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={questionKey}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="w-full h-full"
      >
        <QuestionStep
          question={currentQuestion}
          questionKey={questionKey}
          onOptionSelect={handleOptionSelect}
          orgId={orgId}
        />
      </motion.div>
    </AnimatePresence>
  );
};
